import {
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { RequestStage } from '@internal/ondemand-admin-api-helper';
import {
  SupportReference,
  formatSupportReference,
  getSupportReference,
} from './supportReference';
import { RequestProgress } from './useRequestProgress';

const STEPS: { stage: RequestStage; label: string }[] = [
  { stage: 'preparing', label: 'Preparing' },
  { stage: 'provisioning', label: 'Provisioning' },
  { stage: 'notifying', label: 'Notifying' },
  { stage: 'active', label: 'Active' },
];

const formatSessionEndTime = (sessionEndTime?: string | null) => {
  if (!sessionEndTime) {
    return undefined;
  }
  const endDate = new Date(sessionEndTime);
  return Number.isNaN(endDate.getTime())
    ? sessionEndTime
    : endDate.toLocaleString();
};

interface OndemandAdminRequestProgressProps {
  progress: RequestProgress;
  // Reference from the submit response, used when the status has none
  fallbackReference?: SupportReference;
}

const ReferenceText = ({ reference }: { reference?: SupportReference }) => {
  const text = formatSupportReference(reference);
  return text ? (
    <Typography variant="caption" component="div" style={{ userSelect: 'all' }}>
      {text}
    </Typography>
  ) : null;
};

export function OndemandAdminRequestProgress({
  progress,
  fallbackReference,
}: OndemandAdminRequestProgressProps) {
  const { phase, status } = progress;
  const reference = getSupportReference(status) ?? fallbackReference;

  if (phase === 'idle' || phase === 'unavailable') {
    return null;
  }

  const stage = status?.stage;
  const activeStep =
    stage === 'active'
      ? STEPS.length
      : Math.max(
          STEPS.findIndex(step => step.stage === stage),
          0,
        );
  const sessionEndTime = formatSessionEndTime(status?.sessionEndTime);

  return (
    <div aria-live="polite" style={{ marginTop: 16 }}>
      {stage !== 'failed' && stage !== 'ended' && (
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map(step => (
            <Step key={step.stage}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {phase === 'polling' && (
        <>
          <LinearProgress />
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {status?.message ?? 'Submitting your request...'}
          </Typography>
        </>
      )}

      {phase === 'complete' && stage === 'active' && (
        <Alert severity="success">
          {status?.message}
          {sessionEndTime && ` Your session ends ${sessionEndTime}.`}
        </Alert>
      )}

      {phase === 'complete' && stage === 'ended' && (
        <Alert severity="info">{status?.message}</Alert>
      )}

      {phase === 'complete' && stage === 'failed' && (
        <Alert severity="error">
          {status?.message}
          <ReferenceText reference={reference} />
        </Alert>
      )}

      {phase === 'timedOut' && (
        <Alert severity="info">
          Your request was submitted, but provisioning may still be in progress.
          You will receive an email when access has been provisioned.
          <ReferenceText reference={reference} />
        </Alert>
      )}
    </div>
  );
}
